/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import {
  getTakeoffWizard,
  saveTakeoffWizard,
  removeTakeoffWizard,
  getTakeoffWizardResults,
  isEstimateRequiredPermission,
} from "@Actions/estimation";
import Shimmer from "@Components/Queries/Shimmer";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import IconComponent from "@CubeComponents/icons";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { useLocation } from "react-router-dom";
import FileColumnsTreeView from "@Components/FileColumnsTreeView";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import WizardResults from "./TakeoffWizardResults";
import _ from "lodash";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import { folderPermissions } from "@Helpers/constants";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { useTranslation, untranslated } from "@i18n";
import { dispatchModelLoadingToast } from "@Actions/Viewer";
import { getIsViewerDependencyLoaded } from "@Services/utils/helpers";
import { HoopsViewerContext, CubeGlobal } from "@Features/viewer-core";
import { getFilePropertyNames } from "@Features/viewer-modals/components/Modal/SearchSet/searchSetHelper";
import Text from "@CubeComponents/html/Text/Text";
import { EstimateNotFoundPage } from "@Features/estimation";
import type { RootState } from "src/reducers";
import type { EstimateData as EstimateDataState } from "@ServerTypes/estimates/estimates.schema";
import { Selection } from "@ts3d-hoops/web-viewer";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
const { RESOURCE_MODIFY } = folderPermissions();

type EditableWizardFields = "quantityParameter" | "codeField" | "unitField";

type WizardDetails = {
  _id?: ObjectIdString;
  quantityParameter: string;
  codeField: string;
  unitField: string;
  wizardRunPending?: boolean;
};

/**
 * Local shape of the `filesData` redux slice entries this component reads. `sourceFileVersionId[].id`
 * is a constituent file version _id — the server feeds these to `new ObjectId(id)`
 * (fileUpload.mongo.service.ts), so it carries the ObjectId brand.
 */
type FileVersionData = {
  resourceType?: string;
  sourceFileVersionId?: Array<{ id: ObjectIdString }>;
};

// ADDED: Frontend-only shape of the HoopsViewerContext value. The Hoops viewer instance
// (`viewerImpl`) is dynamically typed by the @ts3d-hoops SDK; we keep it as an opaque
// object whose internals are narrowed at the use site.
type HoopsViewerContextValue = {
  isModelStructureReady?: boolean;
  isSettingsApplied?: boolean;
  viewerImpl?: object;
};

// Derived from server schema: a single wizard measurement entry as returned by
// getTakeoffWizardMeasurementsResponse.result[number]. We additionally require the two
// fields the component reads (elementId/elementFileVersionId).
type WizardMeasurement = NonNullable<
  import("@ServerTypes/takeoffs/takeoffs.schema").GetTakeoffWizardMeasurementsResponse["result"]
>[number] & {
  elementId: number | string;
  elementFileVersionId: string;
};

type FileColumnChange = {
  field: string;
};

type SelectionItemLike = {
  getNodeId: () => number | string;
};

type DropdownSelectArgs = {
  item: {
    id?: string;
  };
};

const TakeoffWizard = ({ fileVersionId }: { fileVersionId: ObjectIdString }) => {
  const { t } = useTranslation();
  const initialWizardData = {
    quantityParameter: "",
    codeField: "",
    unitField: "",
  };

  const [showShimmer, setShowShimmer] = useState(true);
  const [showGetResultsShimmer, setShowGetResultsShimmer] = useState(true);
  const [wizardDetails, setWizardDetails] = useState<WizardDetails>(initialWizardData);
  const [isEmptyData, setIsEmptyData] = useState(false);
  const [fileColumns, setFileColumns] = useState<string[]>([]);
  const [fileColumnsInRequiredFormat, setFileColumnsInRequiredFormat] = useState<
    Array<{ label: string; field: string }>
  >([]);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [openLinkModal, setOpenLinkModal] = useState("");
  const [errorInAnyRecord, setErrorInAnyRecord] = useState(false);
  const [isResultLoadingDone, setIsResultLoadingDone] = useState(false);
  const [isAnyRecordAvailableToAdd, setIsAnyRecordAvailableToAdd] = useState(false);
  const [wizardMeasurements, setWizardMeasurements] = useState<WizardMeasurement[]>([]);
  const [openUndoModal, setOpenUndoModal] = useState(false);

  const { filesData, estimateData, fileversionIdAndNodeOffsetIdMap, modelProperties, estimateError } = useSelector(
    (state: RootState) => ({
      filesData: state.filesData as { [fileVersionId: string]: FileVersionData },
      estimateData: state.estimateData as EstimateDataState & { userRole?: number; costDatabase?: { id?: string } },
      fileversionIdAndNodeOffsetIdMap: state.fileversionIdAndNodeOffsetIdMap,
      modelProperties: state.modelProperties as {
        isLoaded?: boolean;
        questionIdAndNameMapper?: { [k: string]: string };
      },
      estimateError: state.estimateError as boolean | number,
    }),
  );
  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);

  // Entry point: URLSearchParams.get() is outside the type system; the backend route schema
  // validates both ids. Tagged once each, here at their declarations.
  const estimateId = (urlSearchParams.get("estimateId") ?? "") as ObjectIdString;
  const estimateVersionId = urlSearchParams.get("versionId") as ObjectIdString | null;
  const dispatch = useDispatch();
  const resultsGrid = useRef<{
    slickGrid: {
      getDataItem: (row: number) => WizardMeasurement;
      getSelectedRows: () => number[];
    };
  } | null>(null);
  const actionPermissionAllowed = isEstimateRequiredPermission(estimateData, RESOURCE_MODIFY.id);
  const { isModelStructureReady, isSettingsApplied, viewerImpl } = useContext(
    HoopsViewerContext as unknown as React.Context<HoopsViewerContextValue>,
  );
  // ADDED: viewer is the active Hoops viewer instance; `hwv` is the underlying
  // web-viewer object exposed by the @ts3d-hoops SDK (dynamic shape).
  type HwvShape = {
    selectionManager?: {
      add: (item: unknown, preserve?: boolean) => void;
      isNodeSelected: (nodeId: number) => boolean;
      getResults: () => SelectionItemLike[];
      remove: (item: SelectionItemLike) => void;
    };
    model?: { getNodesBounding: (nodeIds: number[]) => Promise<unknown> };
    view?: { fitBounding: (boundingBox: unknown, ms: number) => void };
  };
  const viewer = viewerImpl as { hwv?: HwvShape } | undefined;

  const initWizardData = async () => {
    const response = await getTakeoffWizard({
      estimateId,
      fileVersionId,
      versionId: estimateVersionId ?? undefined,
    });
    if (response.data.result) {
      setWizardDetails(response.data.result as WizardDetails);
      setIsEmptyData(false);
    } else {
      setWizardDetails(initialWizardData);
      setIsEmptyData(true);
    }
    return response;
  };

  const init = async () => {
    let sourceFileVersionIds = [fileVersionId];
    if (filesData[fileVersionId] && filesData[fileVersionId].resourceType == "RESOURCE_TYPE_MERGED_FILE") {
      sourceFileVersionIds = (filesData[fileVersionId].sourceFileVersionId ?? []).map(
        (sourceFile: { id: ObjectIdString }) => sourceFile.id,
      );
    }
    const [columns, propertyNamesResponse] = await getFilePropertyNames(sourceFileVersionIds);
    const columnsArray = Array.isArray(columns) ? columns : [];
    setFileColumns(columnsArray);
    const questionMapper = modelProperties.questionIdAndNameMapper ?? {};
    setFileColumnsInRequiredFormat(
      columnsArray.map((column: string) => {
        let columnName = column;
        if (column.startsWith("customProperties.")) {
          columnName = `customProperties.${questionMapper[column.split(".").slice(1).join(".")] ?? ""}`;
        }
        return {
          label: columnName,
          field: column,
        };
      }),
    );

    const res = await initWizardData();
    if (
      propertyNamesResponse &&
      typeof propertyNamesResponse === "object" &&
      "status" in propertyNamesResponse &&
      propertyNamesResponse.status &&
      res.data.status
    ) {
      setShowShimmer(false);
    }
  };

  useEffect(() => {
    if (modelProperties.isLoaded) {
      init();
    }
  }, [modelProperties.isLoaded]);

  const initResults = useCallback(async () => {
    if (!wizardDetails._id) return;
    const res = await getTakeoffWizardResults({
      wizardId: wizardDetails._id,
      estimateId,
      fileVersionId,
      versionId: estimateVersionId ?? undefined,
    });
    if (res.data.status) {
      setShowGetResultsShimmer(false);
      setWizardMeasurements((res.data.result ?? []) as WizardMeasurement[]);
    } else {
      dispatch(showToastNotification({ content: SOMETHING_WENT_WRONG, type: TOAST_TYPE_ERROR }));
    }
  }, [wizardDetails._id, estimateId, fileVersionId, estimateVersionId]);
  useEffect(() => {
    if (wizardDetails._id && !wizardDetails.wizardRunPending) {
      initResults();
    }
  }, [wizardDetails, initResults]);

  const handleQuantityColumnChange = (field: EditableWizardFields, args: FileColumnChange) => {
    setWizardDetails((prev) => ({ ...prev, [field]: args.field }));
  };

  const closeRunModal = () => {
    setOpenLinkModal("");
  };

  const handleRun = async () => {
    setButtonLoading(true);
    if (openLinkModal == "Details") {
      setOpenLinkModal("Results");
    } else {
      const response = await saveTakeoffWizard({
        estimateId,
        fileVersionId,
        value: { ...wizardDetails },
      });
      if (response.data.status) {
        dispatch(showToastNotification({ content: t("Takeoffs Created Successfully."), type: TOAST_TYPE_SUCCESS }));
        await initWizardData();
      } else {
        dispatch(
          showToastNotification({
            content: response.data.errorMessage ? untranslated(response.data.errorMessage) : SOMETHING_WENT_WRONG,
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
      closeRunModal();
    }
    setButtonLoading(false);
  };

  const emptyWizardTemplate = () => (
    <div className="flex flex-col h-full w-full gap-3">
      <div className="flex w-full h-full items-center justify-center">
        <div className="flex w-60 flex-col gap-4 text-center">
          <IconComponent name="InspectionFormNoData" width="15rem" height="15rem" />
          <Text sx={{ whiteSpace: "normal" }} className="text-text_light font-semibold ">
            {t("Model Parameters not yet linked to CUBE parameters")}
          </Text>
          <div>
            <ButtonComponent
              disabled={!!estimateVersionId || !actionPermissionAllowed || !_.get(estimateData, "costDatabase.id")}
              buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
              onClick={() => setOpenLinkModal("Details")}
            >
              <div className="flex gap-2 items-center">
                <IconComponent name="Link" width="1rem" height="1rem" />
                <Text>{t("Link")}</Text>
              </div>
            </ButtonComponent>
          </div>
        </div>
      </div>
      {!_.get(estimateData, "costDatabase.id") && (
        <Text className="px-5 py-3 text-12 font-normal text-text_light">
          {t(
            "The Model to Takeoff Wizard requires the Estimate to be linked to a Cost Database, which is currently missing. To link a Cost Database please go to the Configurations section of the Estimate.",
          )}
        </Text>
      )}
    </div>
  );

  const wizardFieldsDetails = () => (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <div className="flex gap-2 items-center py-2 text-base font-semibold">
          <IconComponent name="SidebarIcon/Takeoffs" width="1.5rem" height="1.5rem" />
          <span>
            <Text>{t("Takeoff Property")}</Text>
          </span>
          <IconComponent name="details" width="1.25rem" height="1.25rem" />
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3 text-sm font-semibold">
            <div className="flex">
              <span>
                <Text>{t("Quantity Parameter")}</Text>
              </span>
              <span className="text-2xl align-middle font-light text-red-500">*</span>
            </div>
            <FileColumnsTreeView
              change={(e: FileColumnChange) => handleQuantityColumnChange("quantityParameter", e)}
              columns={fileColumnsInRequiredFormat}
              placeholder={t("Select")}
            />
          </div>

          <div className="flex flex-col gap-3 text-sm font-semibold">
            <div className="flex">
              <span>
                <Text>{t("Unit")}</Text>
              </span>
              <span className="text-2xl align-middle font-light text-red-500">*</span>
            </div>
            <FileColumnsTreeView
              change={(e: FileColumnChange) => handleQuantityColumnChange("unitField", e)}
              columns={fileColumnsInRequiredFormat}
              placeholder={t("Select Unit")}
            />
          </div>
        </div>
      </div>

      <div className="flex gap-3 flex-col">
        <div className="flex gap-2 items-center py-2 text-base font-semibold">
          <IconComponent name="basedOnCostDatabase" width="1.5rem" height="1.25rem" />
          <span>
            <Text>{t("Cost Database")}</Text>
          </span>
          <IconComponent name="details" width="1.25rem" height="1.25rem" />
        </div>

        <div className="flex flex-col gap-3 text-sm font-semibold">
          <div className="flex">
            <span>
              <Text>{t("Item Code")}</Text>
            </span>
            <span className="text-2xl align-middle font-light text-red-500">*</span>
          </div>
          <FileColumnsTreeView
            change={(e: FileColumnChange) => handleQuantityColumnChange("codeField", e)}
            columns={fileColumnsInRequiredFormat}
            placeholder={t("Select")}
          />
        </div>
      </div>
    </div>
  );

  const selectButtonTemplate = () => (
    <div id="select">
      <IconComponent
        name="selectElements"
        toolTipContent={t("Select")}
        position="bottom"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          toolbarClickHandler("select");
        }}
        width="1.25rem"
        height="1.25rem"
      />
    </div>
  );

  const isolateBtnTemplate = () => (
    <div id="isolate">
      <IconComponent
        name="Isolate"
        toolTipContent={t("Isolate")}
        position="bottom"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          toolbarClickHandler("isolate");
        }}
        width="1.25rem"
        height="1.25rem"
      />
    </div>
  );

  const highlightBtnTemplate = () => (
    <div id="highlight">
      <IconComponent
        name="Highlight"
        toolTipContent={t("Focus")}
        position="bottom"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          toolbarClickHandler("highlight");
        }}
        width="1.25rem"
        height="1.25rem"
      />
    </div>
  );

  const handle3DElementSelectCallback = (element: WizardMeasurement, action: "select" | "remove") => {
    if (!viewer?.hwv) {
      return;
    }
    const selectionManager = viewer.hwv.selectionManager;
    if (!selectionManager) {
      return;
    }
    const nodeOffset = Number(fileversionIdAndNodeOffsetIdMap[element.elementFileVersionId]);
    const nodeId = Number(element.elementId) + nodeOffset;
    if (action == "select") {
      const selectedNode = Selection.SelectionItem.create(nodeId);
      selectionManager.add(selectedNode, false);
    } else if (action === "remove") {
      if (selectionManager.isNodeSelected(nodeId)) {
        const selectedSelectionItem = selectionManager.getResults();
        const selectionItem = selectedSelectionItem.find(
          (item: SelectionItemLike) => Number(item.getNodeId()) === nodeId,
        );
        selectionItem && selectionManager.remove(selectionItem);
      }
    }
  };

  const removeAllMeasurementsMarkupsFromModel = (records?: WizardMeasurement[]) => {
    const targetRecords = records || wizardMeasurements;
    targetRecords.map((data: WizardMeasurement) => {
      handle3DElementSelectCallback(data, "remove");
    });
  };

  const toggleShowMarkupInViewer = (selectedRecords: WizardMeasurement[]) => {
    removeAllMeasurementsMarkupsFromModel();
    selectedRecords.map((data: WizardMeasurement) => {
      handle3DElementSelectCallback(data, "select");
    });
  };

  const handleFocusClick = async (selectedRecords: WizardMeasurement[]) => {
    toggleShowMarkupInViewer(selectedRecords);
    const nodeIds = getNodeIds(selectedRecords);

    if (nodeIds.length && viewer?.hwv?.model && viewer?.hwv?.view) {
      const boundingBox = await viewer.hwv.model.getNodesBounding(nodeIds);
      viewer.hwv.view.fitBounding(boundingBox, 1000);
    }
  };

  const getNodeIds = (records: WizardMeasurement[]) =>
    records.map((data: WizardMeasurement) => {
      const nodeOffset = Number(fileversionIdAndNodeOffsetIdMap[data.elementFileVersionId]);
      return Number(data.elementId) + nodeOffset;
    });

  const handleIsolateClick = async (selectedRecords: WizardMeasurement[]) => {
    toggleShowMarkupInViewer(selectedRecords);
    const nodeIds = getNodeIds(selectedRecords);
    if (nodeIds.length) await CubeGlobal.isolateNodes(nodeIds);
  };

  const tools = [
    {
      id: "select",
      template: selectButtonTemplate,
    },
    {
      id: "isolate",
      template: isolateBtnTemplate,
    },
    {
      id: "highlight",
      template: highlightBtnTemplate,
    },
  ];

  const toolbarClickHandler = (toolClicked: string) => {
    if (
      getIsViewerDependencyLoaded({
        isModelStructureReady: Boolean(isModelStructureReady),
        isSettingsApplied: Boolean(isSettingsApplied),
        viewerImpl,
        hwv: CubeGlobal.hwv,
      })
    ) {
      const grid = resultsGrid.current?.slickGrid;
      if (!grid) {
        return;
      }
      const selectedRecords = grid.getSelectedRows().map((row: number) => grid.getDataItem(row));
      switch (toolClicked) {
        case "select":
          toggleShowMarkupInViewer(selectedRecords);
          break;

        case "isolate":
          handleIsolateClick(selectedRecords);
          break;

        case "highlight":
          handleFocusClick(selectedRecords);
          break;

        default:
          break;
      }
    } else {
      dispatch(dispatchModelLoadingToast());
    }
  };

  const selectionEvent = () => {};

  const [gridColumns] = useState([
    {
      field: "elementId",
      id: "elementId",
      name: t("CUBE ID"),
      textAlign: "Left",
      minWidth: 100,
    },
    {
      id: "cubeName",
      field: "cubeName",
      name: t("CUBE Name"),
      textAlign: "Left",
      minWidth: 150,
    },
    {
      field: "quantityParameter",
      id: "quantityParameter",
      name: t("QP"),
      textAlign: "Left",
      minWidth: 120,
    },
    {
      field: "code",
      id: "code",
      name: t("CD Code"),
      textAlign: "Left",
      minWidth: 100,
    },
    {
      field: "unit",
      id: "unit",
      name: t("Unit"),
      textAlign: "Left",
      minWidth: 120,
    },
    {
      field: "takeoffName",
      id: "takeoffName",
      name: t("Takeoff Name"),
      textAlign: "Left",
      minWidth: 200,
    },
  ]);

  const resultsWithTakeoffGrid = () => (
    <div className="overflow-auto h-100">
      <SlickGrid1
        ref={resultsGrid as any}
        enableSelectAllCheckbox={true}
        showPageCount={false}
        columnDefinitions={gridColumns as any}
        rowHeight={40 as any}
        dataset={wizardMeasurements as any}
        toolbar={tools}
        // toolbarClickHandler={toolbarClickHandler}
        onSelectedRowIdsChanged={selectionEvent}
        enableFilteringOption
        enableSortingOption
        enableGroupingOption
        enableColumnChooser
        excelExport
        hideNoDataIllustration
      />
    </div>
  );

  const dropDownButtonItems = [
    { text: t("Undo Wizard"), id: "undo", disabled: !actionPermissionAllowed || buttonLoading || !wizardDetails._id },
  ];
  const handleDropDownSelectionHandler = async (args: DropdownSelectArgs) => {
    if (args.item.id === "undo") {
      setOpenUndoModal(true);
    }
  };
  const defaultFieldValue = (field: string) => {
    return typeof field == "string" ? field.split("/").at(-1) : "";
  };

  const resultsDataTemplate = () => {
    if (wizardDetails.wizardRunPending) {
      const isError = !fileColumns.some((column: string) =>
        [wizardDetails.quantityParameter, wizardDetails.unitField, wizardDetails.codeField].includes(column),
      );
      return (
        <div className="border border-button_light flex items-center justify-center pt-4 pb-6">
          <div className="flex w-60 flex-col gap-4 text-center">
            <IconComponent name="InspectionFormNoData" width="15rem" height="15rem" />
            <Text className="text-text_light font-semibold ">
              {" "}
              {isError
                ? t("No results yet. Undo Wizard to re-run the Model to Takeoff Wizard.")
                : t("No results yet, Run Wizard to view Results.")}
            </Text>
            <div>
              <ButtonComponent
                disabled={
                  !!estimateVersionId || !actionPermissionAllowed || !_.get(estimateData, "costDatabase.id") || isError
                }
                buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                onClick={() => setOpenLinkModal("Results")}
              >
                <Text>{t("Run Wizard")}</Text>
              </ButtonComponent>
            </div>
          </div>
        </div>
      );
    } else {
      return showGetResultsShimmer ? <Shimmer /> : resultsWithTakeoffGrid();
    }
  };

  const wizardTemplate = () => (
    <div className="flex flex-col px-6 py-2 gap-6">
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-3">
          <div className="flex justify-between items-center text-base font-semibold py-1">
            <div className="flex gap-2 items-center">
              <IconComponent name="SidebarIcon/Takeoffs" width="1.25rem" height="1.25rem" />
              <Text>{t("Takeoff Property")}</Text>
              <IconComponent name="details" width="1.25rem" height="1.25rem" />
            </div>
            <SelectDropDown
              select={handleDropDownSelectionHandler}
              items={dropDownButtonItems}
              allowButtonPadding={false}
              bgColor="bg-surface_neutral"
              cssClass="e-caret-hide p-0 border-none me -1"
              disabled={!!estimateVersionId || !actionPermissionAllowed}
            >
              <IconComponent name="threedots" width="1.25rem" height="1.25rem" />
            </SelectDropDown>
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-2.5 text-sm font-semibold">
              <Text>{t("Quantity Parameter")}</Text>
              <div className="flex flex-col gap-1">
                <TextFieldComponent disabled defaultValue={defaultFieldValue(wizardDetails.quantityParameter)} />
                {!fileColumns.includes(wizardDetails.quantityParameter) && (
                  <div className="flex items-center gap-1 text-12 text-negative_communication font-normal">
                    <IconComponent name="CloseCancelRed" width="1rem" height="1rem" />
                    <Text>
                      {t(
                        "The above mentioned parameter corresponding to ‘Quantity Parameter’ does not exist in this file.",
                      )}
                    </Text>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2.5 text-sm font-semibold">
              <Text>{t("Unit")}</Text>
              <div className="flex flex-col gap-1">
                <TextFieldComponent disabled defaultValue={defaultFieldValue(wizardDetails.unitField)} />
                {!fileColumns.includes(wizardDetails.unitField) && (
                  <div className="flex items-center gap-1 text-12 text-negative_communication font-normal">
                    <IconComponent name="CloseCancelRed" width="1rem" height="1rem" />
                    <Text>
                      {t("The above mentioned parameter corresponding to ‘Unit’ does not exist in this file.")}
                    </Text>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3 flex-col">
          <div className="flex gap-2 items-center py-1 text-base font-semibold">
            <IconComponent name="basedOnCostDatabase" width="1.25rem" height="1.25rem" />
            <span>
              <Text>{t("Cost Database")}</Text>
            </span>
            <IconComponent name="details" width="1.25rem" height="1.25rem" />
          </div>

          <div className="flex flex-col gap-2.5 text-sm font-semibold">
            <Text>{t("Item Code")}</Text>
            <div className="flex flex-col gap-1">
              <TextFieldComponent disabled defaultValue={defaultFieldValue(wizardDetails.codeField)} />
              {!fileColumns.includes(wizardDetails.codeField) && (
                <div className="flex items-center gap-1 text-12 text-negative_communication font-normal">
                  <IconComponent name="CloseCancelRed" width="1rem" height="1rem" />
                  {"The above mentioned parameter corresponding to ‘Item Code’ does not exist in this file."}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex gap-2 text-base font-semibold">
          <IconComponent name="results" width="1.25rem" height="1.25rem" />
          <Text>{t("Results")}</Text>
        </div>
        {resultsDataTemplate()}
      </div>
    </div>
  );

  const handleUndo = async () => {
    setButtonLoading(true);
    const res = await removeTakeoffWizard({
      estimateId,
      fileVersionId,
      wizardId: wizardDetails._id,
    });
    if (res.data.status) {
      await initWizardData();
      dispatch(
        showToastNotification({
          content: t("Wizard undone successfully. All related Takeoffs and Measurements have been deleted."),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: res.data.errorMessage ? untranslated(res.data.errorMessage) : SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
    setButtonLoading(false);
    closeUndoModal();
  };

  const closeUndoModal = () => {
    setOpenUndoModal(false);
  };

  const undoModalFooter = () => (
    <div className="flex gap-4">
      <ButtonComponent onClick={handleUndo} buttonType={BUTTON_TYPES.GRADIENT_BUTTON} loading={buttonLoading}>
        <Text>{t("Confirm")}</Text>
      </ButtonComponent>
      <ButtonComponent onClick={closeUndoModal} buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} disabled={buttonLoading}>
        <Text>{t("Cancel")}</Text>
      </ButtonComponent>
    </div>
  );

  const undoModalContent = () => (
    <div className="text-sm flex flex-col gap-6">
      <Text>{t("Are you sure you want to proceed?")}</Text>
      <div className="p-4 rounded-lg bg-banner_color_yellow bg-opacity-40">
        <Text sx={{ whiteSpace: "normal" }}>
          {t("Undoing will delete all the Takeoffs and measurements that were created using this wizard.")}
        </Text>
      </div>
    </div>
  );

  const undoModal = () => (
    <ModalStateDialogComponent
      footerContent={undoModalFooter()}
      headerTitle={t("Undo Wizard")}
      isVisible={openUndoModal}
      onCancel={buttonLoading ? undefined : closeUndoModal}
      modelContent={undoModalContent()}
      width="28.125rem"
    />
  );

  const footerConfirmText = () => {
    let text;
    if (openLinkModal == "Details") {
      text = t("Run");
    } else if (errorInAnyRecord) {
      text = t("Proceed Anyway");
    } else {
      text = t("Create Takeoffs");
    }

    return text;
  };

  const createModalFooter = () => (
    <div className="flex gap-4">
      <ButtonComponent
        onClick={handleRun}
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        loading={buttonLoading}
        disabled={
          openLinkModal == "Details"
            ? !wizardDetails.quantityParameter || !wizardDetails.unitField || !wizardDetails.codeField
            : !isResultLoadingDone || !isAnyRecordAvailableToAdd
        }
      >
        {footerConfirmText()}
      </ButtonComponent>
      <ButtonComponent onClick={closeRunModal} buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} disabled={buttonLoading}>
        <Text>{t("Cancel")}</Text>
      </ButtonComponent>
    </div>
  );

  const handleBackClick = () => {
    if (wizardDetails._id) {
      setOpenLinkModal("");
    } else {
      setOpenLinkModal("Details");
    }
    setWizardDetails(initialWizardData);
    setErrorInAnyRecord(false);
    setIsResultLoadingDone(false);
  };

  const createModalHeader = () => (
    <div className="flex gap-4 items-center">
      {openLinkModal == "Results" && (
        <IconComponent
          className="cursor-pointer"
          name="BackwardArrow"
          onClick={handleBackClick}
          width="1.5rem"
          height="1.5rem"
        />
      )}
      <Text>{t("Model to Takeoff Wizard")}</Text>
    </div>
  );

  const createModalContent = () =>
    openLinkModal == "Details" ? (
      wizardFieldsDetails()
    ) : (
      <WizardResults
        estimateId={estimateId}
        fileVersionId={fileVersionId}
        wizardDetails={wizardDetails}
        setErrorInAnyRecord={setErrorInAnyRecord}
        setIsResultLoadingDone={setIsResultLoadingDone}
        isResultLoadingDone={isResultLoadingDone}
        setIsAnyRecordAvailableToAdd={setIsAnyRecordAvailableToAdd}
      />
    );

  const createModal = () => (
    <ModalStateDialogComponent
      footerContent={createModalFooter()}
      headerTitle={createModalHeader()}
      isVisible={!!openLinkModal}
      onCancel={buttonLoading ? undefined : closeRunModal}
      modelContent={createModalContent()}
      width="70%"
      minWidth="25rem"
    />
  );

  return estimateError ? (
    <EstimateNotFoundPage projectId={urlSearchParams.get("projectId")!} />
  ) : (
    <div className="h-full overflow-auto">
      {showShimmer && <Shimmer />}
      {isEmptyData && emptyWizardTemplate()}
      {wizardDetails._id && wizardTemplate()}
      {createModal()}
      {undoModal()}
    </div>
  );
};

export default TakeoffWizard;
